import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useGetDocumentTableOfContent } from 'entities/document';
import { Spinner } from 'app/components';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCaret, useEditorLiveSection, useEditorUiSetters } from 'app/state/contexts/EditorUiContext';

interface TocHeader {
  uuid: string;
  text?: string;
  name?: string;
  type?: string;
  children?: TocHeader[];
  [key: string]: unknown;
}

interface NodeWithChildren {
  uuid?: string;
  children?: NodeWithChildren[];
  [key: string]: unknown;
}

interface DocumentHeadersProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const DocumentHeaders = ({ containerRef }: DocumentHeadersProps) => {
  const { t } = useTranslation('doc-editor');

  const { shadowHost } = useEditorContext();
  const section = useEditorLiveSection();
  const selectedNodeId = useEditorCaret().activeNode?.uuid;
  const { setActiveNode } = useEditorUiSetters();
  const locale = useEditorLocale();

  const { data: pDocument } = useEditorDocument();
  const { data, isLoading } = useGetDocumentTableOfContent(pDocument.revisionId, {
    sectionSuperId: section.superId,
    locale,
    headerHierarchy: ['h1', 'h2'],
  }) as { data: { content?: TocHeader[] } | undefined; isLoading: boolean };

  const onHeaderClick = (superId: string) => {
    const yOffset = -117;
    const element = shadowHost?.shadowRoot?.querySelector(`#anchor-${superId}`);
    if (!element) return;
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    const node = findNode(superId, (section.content as NodeWithChildren[]) ?? []);
    if (node) {
      setActiveNode(node);
    }
  };

  useEffect(() => {
    const element = document.querySelector(`#header-preview-${selectedNodeId}`) as HTMLElement | null;
    const container = containerRef.current;
    if (element && container && shouldScroll(element, container)) {
      const yOffset = -61; // offset for the sticky header
      const y = element.offsetTop + yOffset;
      container.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [selectedNodeId, containerRef]);

  return (
    <div className="preview--container">
      <div className="px-3 pt-4 pb-3 w-100 bg-white sticky-top">
        <div className="fw-bold">{t('sidebar.disposition')}</div>
      </div>
      {isLoading && (
        <div className="px-3">
          <Spinner />
        </div>
      )}
      <div className="px-3 w-100">
        <OrderedList activeId={selectedNodeId} headerList={data?.content} onHeaderClick={onHeaderClick} />
      </div>
    </div>
  );
};

interface OrderedListProps {
  activeId?: string;
  headerList?: TocHeader[];
  onHeaderClick?: (uuid: string) => void;
}

const OrderedList = ({ activeId, headerList, onHeaderClick }: OrderedListProps) => {
  const filteredHeaders = headerList?.filter((header) => header.text !== '') || [];

  if (filteredHeaders.length === 0) {
    return headerList?.map((header) => <OrderedList key={header.uuid} headerList={header.children} />) || null;
  }

  return (
    <ol className="list-unstyled">
      {(headerList ?? []).map((header) => (
        <li key={header.uuid}>
          <button
            id={`header-preview-${header.uuid}`}
            className={headerItemClassName(header, activeId === header.uuid)}
            onClick={() => onHeaderClick?.(header.uuid)}
          >
            {header.name}
          </button>
          <div className="ms-3">
            <OrderedList activeId={activeId} headerList={header.children} onHeaderClick={onHeaderClick} />
          </div>
        </li>
      ))}
    </ol>
  );
};

const shouldScroll = (element: HTMLElement | null, scrollElement: HTMLElement | null) => {
  if (element == null || scrollElement == null) {
    return false;
  }

  const parentHeight = scrollElement.clientHeight;
  const elementTop = element.getBoundingClientRect().top;
  const elementBottom = element.getBoundingClientRect().bottom;

  if (elementTop < 0) {
    return true;
  }

  if (elementBottom > parentHeight) {
    return true;
  }

  return false;
};

function findNode(uuid: string, nodes: NodeWithChildren[]): NodeWithChildren | undefined {
  for (const node of nodes) {
    if (node.uuid === uuid) return node;
    if (node.children) {
      const child = findNode(uuid, node.children);
      if (child) return child;
    }
  }
  return undefined;
}

function headerItemClassName(header: TocHeader, isActive: boolean) {
  let textColor = '';
  let textSize = 'fs-2';

  if (header.type !== 'h1') {
    textColor = 'text-gray-800';
    textSize = 'fs-4';
  }
  if (isActive) {
    textColor = 'text-blue-800';
  }

  return `${textSize} ${textColor} item w-100 border-0 text-start rounded px-2 py-1 mb-2 ${isActive ? 'active bg-blue-200' : 'bg-transparent'}`;
}
